import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Camera } from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Event } from '../types';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../hooks/useCategories';
import CategoryIcon from '../components/CategoryIcon';

const PastEventsPage: React.FC = () => {
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { getCategoryName } = useCategories();

  useEffect(() => {
    const fetchPastEvents = async () => {
      try {
        setLoading(true);
        
        // Get today's date in local timezone
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split('T')[0];
        
        const eventsCollection = collection(db, 'events');
        const q = query(
          eventsCollection,
          where('date', '<', todayStr),
          orderBy('date', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const events = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Event));
        
        setPastEvents(events);
      } catch (err) {
        console.error('Error fetching past events:', err);
        setError('Erro ao carregar eventos passados');
      } finally {
        setLoading(false);
      }
    };

    fetchPastEvents();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Carregando eventos passados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 inline-flex items-center">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Eventos Passados</h1>
        <p className="text-gray-600">
          Reviva os melhores momentos dos eventos que já aconteceram
        </p>
      </div>

      {pastEvents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            Nenhum evento passado encontrado.
          </p>
          <Link
            to="/events"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md transition-colors duration-300"
          >
            Ver Eventos Atuais
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {pastEvents.map(event => (
            <div key={event.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={event.imageUrl} 
                  alt={event.title} 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
                <div className="absolute top-0 right-0 bg-gray-800 text-white px-3 py-1 m-2 rounded-full text-sm font-semibold">
                  <Camera className="h-4 w-4 inline mr-1" />
                  Fotos
                </div>
                <div className="absolute top-0 left-0 bg-red-600 text-white px-3 py-1 m-2 rounded-full text-sm font-semibold">
                  Finalizado
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-1">
                  {event.title}
                </h3>
                
                <div className="flex items-center text-gray-600 mb-2">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span className="text-sm">{event.venue}, {event.city}</span>
                </div>
                
                <div className="flex items-center text-gray-600 mb-3">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="text-sm">
                    {(() => {
                      const parsedDate = parseISO(event.date);
                      return isValid(parsedDate) 
                        ? format(parsedDate, 'dd/MM/yyyy') 
                        : 'Data inválida';
                    })()} • {event.time}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="inline-flex items-center bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full capitalize">
                    <CategoryIcon category={event.category} size={14} className="mr-1" />
                    {getCategoryName(event.category)}
                  </span>
                  
                  <Link 
                    to={`/events/${event.id}`} 
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 flex items-center"
                  >
                    <Camera className="h-4 w-4 mr-1" />
                    Ver Fotos
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PastEventsPage;